/**
 * Inbuilt music notes repository — dense syllabus corpus.
 * All pieces are timed MIDI events (JSON), identical across platforms.
 */

import type { HandId, MusicLibraryIndex, MusicPiece, NoteEvent } from "./types.js";
import { EXPANSION_PIECES } from "./libraryExpand.js";
import { ADVANCED_PIECES } from "./libraryAdvanced.js";
import { MASTERY_PIECES } from "./libraryMastery.js";
import { PRO_PIECES } from "./libraryPro.js";
import { PUBLIC_DOMAIN_PIECES } from "./libraryPublicDomain.js";

function scaleAscDesc(
  startMidi: number,
  degrees: number[],
  beatStep = 0.5,
  hand: HandId = "right",
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  let beat = 0;
  const up = degrees.map((d) => startMidi + d);
  const down = [...up].reverse().slice(1);
  const midis = [...up, ...down];
  // Standard one-octave C-major style fingering (RH / LH).
  const rhFingers: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 1, 2, 3, 4, 5, 4, 3, 2, 1, 3, 2, 1];
  const lhFingers: Array<1 | 2 | 3 | 4 | 5> = [5, 4, 3, 2, 1, 3, 2, 1, 2, 3, 1, 2, 3, 4, 5];
  const fingers = hand === "left" ? lhFingers : rhFingers;
  midis.forEach((midi, i) => {
    notes.push({
      beat,
      duration: beatStep,
      midi,
      velocity: 90,
      hand,
      finger: fingers[i] ?? (hand === "left" ? 3 : 1),
    });
    beat += beatStep;
  });
  return notes;
}

function melody(
  events: Array<[number, number, number, number?]>,
  hand: HandId = "right",
): NoteEvent[] {
  return events.map(([beat, duration, midi, finger]) => ({
    beat,
    duration,
    midi,
    velocity: 92,
    hand,
    finger: finger as 1 | 2 | 3 | 4 | 5 | undefined,
  }));
}

/** Sequential single notes with a shared duration. */
function sequence(
  midis: number[],
  beatStep: number,
  hand: HandId = "right",
  startBeat = 0,
): NoteEvent[] {
  return midis.map((midi, i) => ({
    beat: startBeat + i * beatStep,
    duration: beatStep,
    midi,
    velocity: 90,
    hand,
  }));
}

/** Block chord hits at listed beats. */
function chordHits(
  midis: number[],
  beats: number[],
  duration: number,
  hand: HandId = "right",
): NoteEvent[] {
  const notes: NoteEvent[] = [];
  for (const beat of beats) {
    for (const midi of midis) {
      notes.push({ beat, duration, midi, velocity: 95, hand });
    }
  }
  return notes;
}

function piece(
  partial: Omit<MusicPiece, "notes"> & { notes: NoteEvent[] },
): MusicPiece {
  return partial;
}

/* —— Stage 1 foundations —— */

const FIVE_FINGER_C = piece({
  id: "ex-five-finger-c",
  title: "Five-Finger Drill (C Position RH)",
  category: "exercise",
  bpm: 60,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Fingers 1–5 on C4–G4 ascending and descending.",
  notes: melody([
    [0, 1, 60, 1],
    [1, 1, 62, 2],
    [2, 1, 64, 3],
    [3, 1, 65, 4],
    [4, 1, 67, 5],
    [5, 1, 65, 4],
    [6, 1, 64, 3],
    [7, 1, 62, 2],
    [8, 2, 60, 1],
  ]),
});

const FIVE_FINGER_C_LH = piece({
  id: "ex-five-finger-c-lh",
  title: "Five-Finger Drill (C Position LH)",
  category: "exercise",
  bpm: 60,
  timeSignature: "4/4",
  key: "C major",
  hand: "left",
  stage: 1,
  description: "LH five-finger C2–G2 (MIDI 36–43 area uses C3–G3).",
  notes: melody(
    [
      [0, 1, 48, 5],
      [1, 1, 50, 4],
      [2, 1, 52, 3],
      [3, 1, 53, 2],
      [4, 1, 55, 1],
      [5, 1, 53, 2],
      [6, 1, 52, 3],
      [7, 1, 50, 4],
      [8, 2, 48, 5],
    ],
    "left",
  ),
});

const FINGER_SKIPS_C = piece({
  id: "ex-finger-skips-c",
  title: "Finger Skips (C–E–G)",
  category: "exercise",
  bpm: 66,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Skip patterns inside C position — prepare for chords.",
  notes: melody([
    [0, 1, 60, 1],
    [1, 1, 64, 3],
    [2, 1, 67, 5],
    [3, 1, 64, 3],
    [4, 1, 60, 1],
    [5, 1, 64, 3],
    [6, 1, 62, 2],
    [7, 1, 65, 4],
    [8, 2, 67, 5],
  ]),
});

const REPEAT_HOLD_C = piece({
  id: "ex-repeat-hold-c",
  title: "Repeat & Hold (C Position)",
  category: "exercise",
  bpm: 56,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Same finger twice, then long hold — control and release.",
  notes: melody([
    [0, 0.5, 60, 1],
    [0.5, 0.5, 60, 1],
    [1, 2, 60, 1],
    [3, 0.5, 64, 3],
    [3.5, 0.5, 64, 3],
    [4, 2, 64, 3],
    [6, 0.5, 67, 5],
    [6.5, 0.5, 67, 5],
    [7, 2, 67, 5],
  ]),
});

const REPEAT_HOLD_C_LH = piece({
  id: "ex-repeat-hold-c-lh",
  title: "Repeat & Hold (C Position LH)",
  category: "exercise",
  bpm: 56,
  timeSignature: "4/4",
  key: "C major",
  hand: "left",
  stage: 1,
  description: "LH mirror of repeat & hold — finger 5 on C, thumb on G.",
  notes: melody(
    [
      [0, 0.5, 48, 5],
      [0.5, 0.5, 48, 5],
      [1, 2, 48, 5],
      [3, 0.5, 52, 3],
      [3.5, 0.5, 52, 3],
      [4, 2, 52, 3],
      [6, 0.5, 55, 1],
      [6.5, 0.5, 55, 1],
      [7, 2, 55, 1],
    ],
    "left",
  ),
});

const FINGER_SKIPS_C_LH = piece({
  id: "ex-finger-skips-c-lh",
  title: "Finger Skips (C–E–G LH)",
  category: "exercise",
  bpm: 66,
  timeSignature: "4/4",
  key: "C major",
  hand: "left",
  stage: 1,
  description: "LH skip patterns inside C position — prepare for chords later.",
  notes: melody(
    [
      [0, 1, 48, 5],
      [1, 1, 52, 3],
      [2, 1, 55, 1],
      [3, 1, 52, 3],
      [4, 1, 48, 5],
      [5, 1, 52, 3],
      [6, 1, 50, 4],
      [7, 1, 53, 2],
      [8, 2, 55, 1],
    ],
    "left",
  ),
});

const RHYTHM_WHOLE_HALF = piece({
  id: "rhythm-whole-half-c",
  title: "Whole & Half Notes on C",
  category: "exercise",
  bpm: 72,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Long values on a single pitch — metronome breathe.",
  notes: melody([
    [0, 4, 60],
    [4, 2, 64],
    [6, 2, 64],
    [8, 4, 67],
    [12, 2, 60],
    [14, 2, 60],
  ]),
});

const RHYTHM_QUARTERS = piece({
  id: "rhythm-quarters-c",
  title: "Steady Quarters (C–G)",
  category: "exercise",
  bpm: 80,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Even quarter notes walking the five-finger row.",
  notes: sequence([60, 62, 64, 65, 67, 65, 64, 62, 60, 62, 64, 65, 67, 65, 64, 60], 1),
});

const RHYTHM_EIGHTHS = piece({
  id: "rhythm-eighths-c",
  title: "Eighth-Note Walk (C Position)",
  category: "exercise",
  bpm: 76,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Subdivision feel with even eighths.",
  notes: sequence([60, 62, 64, 65, 67, 65, 64, 62, 60, 64, 67, 64, 60], 0.5),
});

const C_MAJOR_RH = piece({
  id: "scale-c-major-rh",
  title: "C Major Scale (RH 1 octave)",
  category: "scale",
  bpm: 80,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "One-octave C major ascending and descending from Middle C.",
  notes: scaleAscDesc(60, [0, 2, 4, 5, 7, 9, 11, 12]),
});

const C_MAJOR_LH = piece({
  id: "scale-c-major-lh",
  title: "C Major Scale (LH 1 octave)",
  category: "scale",
  bpm: 72,
  timeSignature: "4/4",
  key: "C major",
  hand: "left",
  stage: 1,
  description: "One-octave C major in the bass register.",
  notes: scaleAscDesc(48, [0, 2, 4, 5, 7, 9, 11, 12], 0.5, "left"),
});

const MARY_LAMB = piece({
  id: "melody-mary-lamb",
  title: "Mary Had a Little Lamb",
  category: "melody",
  bpm: 92,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Beginner melody entirely in C position.",
  notes: melody([
    [0, 1, 64],
    [1, 1, 62],
    [2, 1, 60],
    [3, 1, 62],
    [4, 1, 64],
    [5, 1, 64],
    [6, 2, 64],
    [8, 1, 62],
    [9, 1, 62],
    [10, 2, 62],
    [12, 1, 64],
    [13, 1, 67],
    [14, 2, 67],
  ]),
});

const HOT_CROSS = piece({
  id: "melody-hot-cross",
  title: "Hot Cross Buns",
  category: "melody",
  bpm: 88,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Three-note beginner tune (E–D–C).",
  notes: melody([
    [0, 1, 64],
    [1, 1, 62],
    [2, 2, 60],
    [4, 1, 64],
    [5, 1, 62],
    [6, 2, 60],
    [8, 0.5, 60],
    [8.5, 0.5, 60],
    [9, 0.5, 62],
    [9.5, 0.5, 62],
    [10, 0.5, 64],
    [10.5, 0.5, 62],
    [11, 2, 60],
  ]),
});

const ODE_TO_JOY = piece({
  id: "melody-ode-to-joy",
  title: "Ode to Joy (Excerpt)",
  category: "melody",
  bpm: 96,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Familiar Beethoven theme for early sight / ear practice.",
  notes: melody([
    [0, 1, 64],
    [1, 1, 64],
    [2, 1, 65],
    [3, 1, 67],
    [4, 1, 67],
    [5, 1, 65],
    [6, 1, 64],
    [7, 1, 62],
    [8, 1, 60],
    [9, 1, 60],
    [10, 1, 62],
    [11, 1, 64],
    [12, 1.5, 64],
    [13.5, 0.5, 62],
    [14, 2, 62],
  ]),
});

const TWINKLE = piece({
  id: "melody-twinkle",
  title: "Twinkle Twinkle (Excerpt)",
  category: "melody",
  bpm: 88,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 1,
  description: "Classic beginner melody in C position.",
  notes: melody([
    [0, 1, 60],
    [1, 1, 60],
    [2, 1, 67],
    [3, 1, 67],
    [4, 1, 69],
    [5, 1, 69],
    [6, 2, 67],
    [8, 1, 65],
    [9, 1, 65],
    [10, 1, 64],
    [11, 1, 64],
    [12, 1, 62],
    [13, 1, 62],
    [14, 2, 60],
  ]),
});

/* —— Stage 2 — scales, black keys, syncopation —— */

const G_MAJOR_RH = piece({
  id: "scale-g-major-rh",
  title: "G Major Scale (RH)",
  category: "scale",
  bpm: 80,
  timeSignature: "4/4",
  key: "G major",
  hand: "right",
  stage: 2,
  description: "G major with F# — black key introduction.",
  notes: scaleAscDesc(67, [0, 2, 4, 5, 7, 9, 11, 12]),
});

const G_MAJOR_LH = piece({
  id: "scale-g-major-lh",
  title: "G Major Scale (LH)",
  category: "scale",
  bpm: 76,
  timeSignature: "4/4",
  key: "G major",
  hand: "left",
  stage: 2,
  description: "G major one octave left hand.",
  notes: scaleAscDesc(55, [0, 2, 4, 5, 7, 9, 11, 12], 0.5, "left"),
});

const D_MAJOR_RH = piece({
  id: "scale-d-major-rh",
  title: "D Major Scale (RH)",
  category: "scale",
  bpm: 78,
  timeSignature: "4/4",
  key: "D major",
  hand: "right",
  stage: 2,
  description: "D major — F# and C#.",
  notes: scaleAscDesc(62, [0, 2, 4, 5, 7, 9, 11, 12]),
});

const F_MAJOR_RH = piece({
  id: "scale-f-major-rh",
  title: "F Major Scale (RH)",
  category: "scale",
  bpm: 78,
  timeSignature: "4/4",
  key: "F major",
  hand: "right",
  stage: 2,
  description: "F major with Bb — flat introduction.",
  notes: scaleAscDesc(65, [0, 2, 4, 5, 7, 9, 10, 12]),
});

const A_MINOR_RH = piece({
  id: "scale-a-minor-rh",
  title: "A Natural Minor (RH)",
  category: "scale",
  bpm: 76,
  timeSignature: "4/4",
  key: "A minor",
  hand: "right",
  stage: 2,
  description: "Relative minor of C — white keys from A.",
  notes: scaleAscDesc(69, [0, 2, 3, 5, 7, 8, 10, 12]),
});

const C_MAJOR_2OCT = piece({
  id: "scale-c-major-2oct-rh",
  title: "C Major Scale (RH 2 octaves)",
  category: "scale",
  bpm: 72,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Thumb-tuck through two octaves.",
  notes: scaleAscDesc(60, [0, 2, 4, 5, 7, 9, 11, 12, 14, 16, 17, 19, 21, 23, 24], 0.5),
});

const BLACK_KEY_FS = piece({
  id: "ex-black-keys-fs",
  title: "Black-Key Landmarks (F# / Bb)",
  category: "exercise",
  bpm: 70,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Target sharps and flats between white neighbors.",
  notes: melody([
    [0, 1, 65],
    [1, 1, 66],
    [2, 1, 67],
    [3, 1, 66],
    [4, 1, 65],
    [5, 1, 70],
    [6, 1, 69],
    [7, 1, 70],
    [8, 2, 72],
  ]),
});

const CHROMATIC_SHORT = piece({
  id: "ex-chromatic-c-e",
  title: "Chromatic Walk C–E",
  category: "exercise",
  bpm: 66,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Half-step control onto black and white keys.",
  notes: sequence([60, 61, 62, 63, 64, 63, 62, 61, 60], 0.5),
});

const C_MAJOR_ARPEGGIO = piece({
  id: "arp-c-major",
  title: "C Major Arpeggio",
  category: "arpeggio",
  bpm: 70,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Broken C–E–G–C triad ascending and descending.",
  notes: scaleAscDesc(60, [0, 4, 7, 12], 0.5),
});

const G_MAJOR_ARPEGGIO = piece({
  id: "arp-g-major",
  title: "G Major Arpeggio",
  category: "arpeggio",
  bpm: 70,
  timeSignature: "4/4",
  key: "G major",
  hand: "right",
  stage: 2,
  description: "Broken G–B–D–G.",
  notes: scaleAscDesc(67, [0, 4, 7, 12], 0.5),
});

const F_MAJOR_ARPEGGIO = piece({
  id: "arp-f-major",
  title: "F Major Arpeggio",
  category: "arpeggio",
  bpm: 68,
  timeSignature: "4/4",
  key: "F major",
  hand: "right",
  stage: 2,
  description: "Broken F–A–C–F.",
  notes: scaleAscDesc(65, [0, 4, 7, 12], 0.5),
});

const SYNCOP_OFFBEAT = piece({
  id: "rhythm-syncop-offbeat",
  title: "Off-Beat Syncopation (C)",
  category: "exercise",
  bpm: 84,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Attacks on the & of the beat — syncopation prep.",
  notes: melody([
    [0.5, 0.5, 60],
    [1.5, 0.5, 64],
    [2.5, 0.5, 67],
    [3.5, 0.5, 64],
    [4.5, 0.5, 60],
    [5.5, 0.5, 62],
    [6.5, 0.5, 64],
    [7.5, 0.5, 60],
  ]),
});

const SYNCOP_ANTICIPATE = piece({
  id: "rhythm-syncop-anticipate",
  title: "Anticipation Syncopation",
  category: "exercise",
  bpm: 80,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Arrive early on weak beats, then land the downbeat.",
  notes: melody([
    [0, 1, 60],
    [1.5, 0.5, 64],
    [2, 1, 67],
    [3.5, 0.5, 64],
    [4, 1, 60],
    [5.5, 0.5, 62],
    [6, 2, 60],
  ]),
});

const FRERE = piece({
  id: "melody-frere-jacques",
  title: "Frère Jacques (Excerpt)",
  category: "melody",
  bpm: 90,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 2,
  description: "Round melody — scale steps and thirds.",
  notes: melody([
    [0, 1, 60],
    [1, 1, 62],
    [2, 1, 64],
    [3, 1, 60],
    [4, 1, 60],
    [5, 1, 62],
    [6, 1, 64],
    [7, 1, 60],
    [8, 1, 64],
    [9, 1, 65],
    [10, 2, 67],
    [12, 1, 64],
    [13, 1, 65],
    [14, 2, 67],
  ]),
});

/* —— Stage 3 — harmony —— */

const C_MAJOR_TRIAD = piece({
  id: "chord-c-major-triad",
  title: "C Major Triad Block",
  category: "chord",
  bpm: 60,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 3,
  description: "Play C–E–G together as block chords.",
  notes: chordHits([60, 64, 67], [0, 2, 4], 1).concat(
    chordHits([60, 64, 67], [6], 2),
  ),
});

const G_MAJOR_TRIAD = piece({
  id: "chord-g-major-triad",
  title: "G Major Triad Block",
  category: "chord",
  bpm: 60,
  timeSignature: "4/4",
  key: "G major",
  hand: "right",
  stage: 3,
  description: "G–B–D blocks.",
  notes: chordHits([67, 71, 74], [0, 2, 4, 6], 1),
});

const F_MAJOR_TRIAD = piece({
  id: "chord-f-major-triad",
  title: "F Major Triad Block",
  category: "chord",
  bpm: 60,
  timeSignature: "4/4",
  key: "F major",
  hand: "right",
  stage: 3,
  description: "F–A–C blocks.",
  notes: chordHits([65, 69, 72], [0, 2, 4, 6], 1),
});

const A_MINOR_TRIAD = piece({
  id: "chord-a-minor-triad",
  title: "A Minor Triad Block",
  category: "chord",
  bpm: 60,
  timeSignature: "4/4",
  key: "A minor",
  hand: "right",
  stage: 3,
  description: "A–C–E minor quality.",
  notes: chordHits([69, 72, 76], [0, 2, 4, 6], 1),
});

const C_INV1 = piece({
  id: "chord-c-inv1",
  title: "C Major 1st Inversion",
  category: "chord",
  bpm: 64,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 3,
  description: "E–G–C shape (1st inversion).",
  notes: chordHits([64, 67, 72], [0, 2, 4, 6], 1),
});

const C_INV2 = piece({
  id: "chord-c-inv2",
  title: "C Major 2nd Inversion",
  category: "chord",
  bpm: 64,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 3,
  description: "G–C–E shape (2nd inversion).",
  notes: chordHits([67, 72, 76], [0, 2, 4, 6], 1),
});

const I_IV_V_C = piece({
  id: "chord-i-iv-v-c",
  title: "I–IV–V–I in C",
  category: "chord",
  bpm: 66,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 3,
  description: "Classic cadence blocks: C → F → G → C.",
  notes: [
    ...chordHits([60, 64, 67], [0], 2),
    ...chordHits([65, 69, 72], [2], 2),
    ...chordHits([67, 71, 74], [4], 2),
    ...chordHits([60, 64, 67], [6], 2),
  ],
});

const BROKEN_TRIADS = piece({
  id: "arp-broken-triads-c",
  title: "Broken Triads C–F–G",
  category: "arpeggio",
  bpm: 72,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 3,
  description: "Alberti-style broken chords across I–IV–V.",
  notes: sequence(
    [60, 64, 67, 64, 65, 69, 72, 69, 67, 71, 74, 71, 60, 64, 67, 60],
    0.5,
  ),
});

const BOTH_SIMPLE = piece({
  id: "both-bass-drone-melody",
  title: "LH Drone + RH Melody",
  category: "melody",
  bpm: 80,
  timeSignature: "4/4",
  key: "C major",
  hand: "both",
  stage: 3,
  description: "Independence seed: held bass C with RH Mary theme.",
  notes: [
    { beat: 0, duration: 8, midi: 48, velocity: 70, hand: "left", finger: 5 },
    ...melody([
      [0, 1, 64],
      [1, 1, 62],
      [2, 1, 60],
      [3, 1, 62],
      [4, 1, 64],
      [5, 1, 64],
      [6, 2, 64],
    ]),
  ],
});

const BOTH_ALT = piece({
  id: "both-hand-alternation",
  title: "Hand Alternation (LH–RH)",
  category: "exercise",
  bpm: 76,
  timeSignature: "4/4",
  key: "C major",
  hand: "both",
  stage: 3,
  description: "Alternate bass then treble on the same harmony.",
  notes: [
    { beat: 0, duration: 1, midi: 48, velocity: 88, hand: "left" },
    { beat: 1, duration: 1, midi: 64, velocity: 90, hand: "right" },
    { beat: 2, duration: 1, midi: 52, velocity: 88, hand: "left" },
    { beat: 3, duration: 1, midi: 67, velocity: 90, hand: "right" },
    { beat: 4, duration: 1, midi: 55, velocity: 88, hand: "left" },
    { beat: 5, duration: 1, midi: 64, velocity: 90, hand: "right" },
    { beat: 6, duration: 1, midi: 48, velocity: 88, hand: "left" },
    { beat: 7, duration: 1, midi: 60, velocity: 90, hand: "right" },
  ],
});

/* —— Stage 4 — sight / ear / blind —— */

const LONDON_BRIDGE = piece({
  id: "melody-london-bridge",
  title: "London Bridge",
  category: "melody",
  bpm: 96,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 4,
  description: "Sight-reading folk melody.",
  notes: melody([
    [0, 1, 67],
    [1, 1, 65],
    [2, 1, 64],
    [3, 1, 65],
    [4, 1, 67],
    [5, 1, 67],
    [6, 2, 67],
    [8, 1, 65],
    [9, 1, 65],
    [10, 2, 65],
    [12, 1, 67],
    [13, 1, 72],
    [14, 2, 72],
  ]),
});

const INTERVAL_JUMPS = piece({
  id: "ex-interval-jumps",
  title: "Interval Jumps (3rds–5ths)",
  category: "exercise",
  bpm: 70,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 4,
  description: "Ear + sight leaps without stepwise crutches.",
  notes: melody([
    [0, 1, 60],
    [1, 1, 64],
    [2, 1, 60],
    [3, 1, 67],
    [4, 1, 62],
    [5, 1, 69],
    [6, 1, 64],
    [7, 1, 72],
    [8, 2, 60],
  ]),
});

const EAR_ECHO = piece({
  id: "ex-ear-echo-c",
  title: "Echo Patterns (C Position)",
  category: "exercise",
  bpm: 74,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 4,
  description: "Short motivic cells for memory / ear echo.",
  notes: melody([
    [0, 0.5, 60],
    [0.5, 0.5, 62],
    [1, 1, 64],
    [2, 0.5, 64],
    [2.5, 0.5, 62],
    [3, 1, 60],
    [4, 0.5, 62],
    [4.5, 0.5, 64],
    [5, 0.5, 65],
    [5.5, 0.5, 67],
    [6, 2, 60],
  ]),
});

const BLIND_SCALE_C = piece({
  id: "ex-blind-scale-c",
  title: "Blind C Major (muscle memory)",
  category: "scale",
  bpm: 88,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 4,
  description: "Same as C scale — intended for helper-off practice.",
  notes: scaleAscDesc(60, [0, 2, 4, 5, 7, 9, 11, 12], 0.5),
});

const BLIND_ARP_C = piece({
  id: "ex-blind-arp-c",
  title: "Blind C Arpeggio",
  category: "arpeggio",
  bpm: 84,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 4,
  description: "Arpeggio without visual crutches.",
  notes: scaleAscDesc(60, [0, 4, 7, 12, 16, 19, 24], 0.5),
});

const SIGHT_MIX = piece({
  id: "melody-sight-mix-a",
  title: "Sight Mix A",
  category: "melody",
  bpm: 86,
  timeSignature: "3/4",
  key: "C major",
  hand: "right",
  stage: 4,
  description: "Waltz meter sight line mixing steps and skips.",
  notes: melody([
    [0, 1, 60],
    [1, 1, 64],
    [2, 1, 67],
    [3, 1, 65],
    [4, 1, 62],
    [5, 1, 60],
    [6, 1, 64],
    [7, 1, 67],
    [8, 2, 72],
  ]),
});

/* —— Stage 5 — speed / polyrhythm prep —— */

const POLY_3_2_PREP = piece({
  id: "rhythm-poly-3against2-prep",
  title: "3-against-2 Prep (grouped)",
  category: "exercise",
  bpm: 60,
  timeSignature: "6/8",
  key: "C major",
  hand: "right",
  stage: 5,
  description: "Grouped twos vs threes on one staff — polyrhythm gateway.",
  notes: melody([
    [0, 1, 60],
    [1, 1, 64],
    [2, 1, 67],
    [3, 1.5, 60],
    [4.5, 1.5, 64],
    [6, 1, 67],
    [7, 1, 64],
    [8, 1, 60],
  ]),
});

const POLY_4_3_PREP = piece({
  id: "rhythm-poly-4against3-prep",
  title: "4-against-3 Prep",
  category: "exercise",
  bpm: 56,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 5,
  description: "Subdivision cells alternating 4 and 3 groupings.",
  notes: melody([
    [0, 0.25, 60],
    [0.25, 0.25, 62],
    [0.5, 0.25, 64],
    [0.75, 0.25, 65],
    [1, 1 / 3, 67],
    [1 + 1 / 3, 1 / 3, 65],
    [1 + 2 / 3, 1 / 3, 64],
    [2, 0.25, 62],
    [2.25, 0.25, 64],
    [2.5, 0.25, 65],
    [2.75, 0.25, 67],
    [3, 1, 60],
  ]),
});

const SCALE_BURST_C = piece({
  id: "scale-burst-c-sixteenths",
  title: "C Major Burst (sixteenths)",
  category: "scale",
  bpm: 100,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 5,
  description: "High NPM scale burst — sixteenth grid.",
  notes: scaleAscDesc(60, [0, 2, 4, 5, 7, 9, 11, 12], 0.25),
});

const SCALE_BURST_G = piece({
  id: "scale-burst-g-sixteenths",
  title: "G Major Burst (sixteenths)",
  category: "scale",
  bpm: 96,
  timeSignature: "4/4",
  key: "G major",
  hand: "right",
  stage: 5,
  description: "G major speed burst.",
  notes: scaleAscDesc(67, [0, 2, 4, 5, 7, 9, 11, 12], 0.25),
});

const ARP_BURST_C = piece({
  id: "arp-burst-c-2oct",
  title: "C Arpeggio Burst (2 oct)",
  category: "arpeggio",
  bpm: 110,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 5,
  description: "Wide arpeggio at elevated tempo.",
  notes: scaleAscDesc(60, [0, 4, 7, 12, 16, 19, 24], 0.25),
});

const CHROMATIC_BURST = piece({
  id: "ex-chromatic-burst",
  title: "Chromatic Burst C–G",
  category: "exercise",
  bpm: 92,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 5,
  description: "Fast half-step run for agility.",
  notes: sequence([60, 61, 62, 63, 64, 65, 66, 67, 66, 65, 64, 63, 62, 61, 60], 0.25),
});

const THIRDS_C = piece({
  id: "ex-scales-in-thirds-c",
  title: "C Major in Broken Thirds",
  category: "exercise",
  bpm: 84,
  timeSignature: "4/4",
  key: "C major",
  hand: "right",
  stage: 5,
  description: "Broken diatonic thirds — articulation masterclass.",
  notes: sequence(
    [60, 64, 62, 65, 64, 67, 65, 69, 67, 71, 69, 72, 71, 69, 67, 65, 64, 62, 60],
    0.5,
  ),
});

export const BUILTIN_LIBRARY: MusicLibraryIndex = {
  version: 2,
  pieces: [
    // Stage 1
    FIVE_FINGER_C,
    FIVE_FINGER_C_LH,
    FINGER_SKIPS_C,
    FINGER_SKIPS_C_LH,
    REPEAT_HOLD_C,
    REPEAT_HOLD_C_LH,
    RHYTHM_WHOLE_HALF,
    RHYTHM_QUARTERS,
    RHYTHM_EIGHTHS,
    C_MAJOR_RH,
    C_MAJOR_LH,
    MARY_LAMB,
    HOT_CROSS,
    ODE_TO_JOY,
    TWINKLE,
    // Stage 2
    G_MAJOR_RH,
    G_MAJOR_LH,
    D_MAJOR_RH,
    F_MAJOR_RH,
    A_MINOR_RH,
    C_MAJOR_2OCT,
    BLACK_KEY_FS,
    CHROMATIC_SHORT,
    C_MAJOR_ARPEGGIO,
    G_MAJOR_ARPEGGIO,
    F_MAJOR_ARPEGGIO,
    SYNCOP_OFFBEAT,
    SYNCOP_ANTICIPATE,
    FRERE,
    // Stage 3
    C_MAJOR_TRIAD,
    G_MAJOR_TRIAD,
    F_MAJOR_TRIAD,
    A_MINOR_TRIAD,
    C_INV1,
    C_INV2,
    I_IV_V_C,
    BROKEN_TRIADS,
    BOTH_SIMPLE,
    BOTH_ALT,
    // Stage 4
    LONDON_BRIDGE,
    INTERVAL_JUMPS,
    EAR_ECHO,
    BLIND_SCALE_C,
    BLIND_ARP_C,
    SIGHT_MIX,
    // Stage 5
    POLY_3_2_PREP,
    POLY_4_3_PREP,
    SCALE_BURST_C,
    SCALE_BURST_G,
    ARP_BURST_C,
    CHROMATIC_BURST,
    THIRDS_C,
    ...EXPANSION_PIECES,
    ...ADVANCED_PIECES,
    ...MASTERY_PIECES,
    ...PRO_PIECES,
    ...PUBLIC_DOMAIN_PIECES,
  ],
};

export function listPieces(): MusicPiece[] {
  return BUILTIN_LIBRARY.pieces;
}

export function getPieceById(id: string): MusicPiece | undefined {
  return BUILTIN_LIBRARY.pieces.find((p) => p.id === id);
}

export function listPiecesByCategory(
  category: MusicPiece["category"],
): MusicPiece[] {
  return BUILTIN_LIBRARY.pieces.filter((p) => p.category === category);
}

export function listPiecesByStage(stage: number): MusicPiece[] {
  return BUILTIN_LIBRARY.pieces.filter((p) => p.stage === stage);
}

export function pieceDurationBeats(piece: MusicPiece): number {
  let end = 0;
  for (const n of piece.notes) {
    end = Math.max(end, n.beat + n.duration);
  }
  return end;
}

/** Default-minute sum helper for docs / tooling. */
export function libraryPieceCount(): number {
  return BUILTIN_LIBRARY.pieces.length;
}
